import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { round2, useAppContext } from "./AppContext";
import { runPaymentInstallmentPricing } from "../pricing/paymentInstallmentsPricing";
import { compactPricingAuditMeta } from "../pricing/compactPricingAuditMeta";
import {
  buildLdsSplitCardsFromOrder,
  ldsSplitOrderCardsFingerprint,
  mergeLdsSplitCardsAgentPrefs,
  resolveLdsActiveCardIndex,
  traceLdsSplitPaymentsSsot,
} from "../utils/orders/ldsSplitPaymentCards";
import { isLdsSplitPaymentSlotLocked } from "../utils/orders/ldsSplitPaymentSlotLock";
import { parseLdsOrderPayments } from "../utils/orders/ldsSplitPaymentsSsot";
import { resolveLdsFinancialState } from "../utils/orders/resolveLdsFinancialState";
import { consumeHrFillThenPayPaymentPrefs } from "../utils/dynamicPackage/hrFillThenPayPaymentPrefs";

const CardsContext = createContext();

let EMPTY_CARD = {
  numberofpayments: 1,
  paymentsInterest: 0,
  amount: 0,
  amountWithInterest: 0,
  paid: false,
  ldsSlotLocked: false,
};

export { EMPTY_CARD };

function buildSplitCardsFromPrefs(totalIls, prefs, interestTable, pricingAuditMeta) {
  const total = round2(Number(totalIls) || 0);
  const count = Math.min(4, Math.max(1, Number(prefs?.cardCount) || 1));
  const base = Math.floor(total / count);
  let remainder = total - base * count;
  const cards = [];
  for (let i = 0; i < count; i += 1) {
    const amount = round2(base + (remainder > 0 ? 1 : 0));
    if (remainder > 0) remainder -= 1;
    const numberOfPayments = Math.min(
      12,
      Math.max(
        1,
        Number(prefs?.installmentsByCard?.[i] ?? prefs?.installments) || 1
      )
    );
    const priced = runPaymentInstallmentPricing({
      principalIls: amount,
      numberOfPayments,
      interestTable,
      pricingAuditMeta: compactPricingAuditMeta({
        ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
          ? pricingAuditMeta
          : {}),
        installmentsSelected: numberOfPayments,
      }),
    });
    cards.push({
      ...EMPTY_CARD,
      amount,
      numberofpayments: numberOfPayments,
      paymentsInterest: priced.paymentInterest,
      paymentInterest: priced.paymentInterest,
      amountWithInterest: priced.amountWithInterest,
      paymentAction: priced.paymentAction,
      paymentPricingAuditSteps: priced.auditSteps,
    });
  }
  return cards;
}

function cardsStructureMatches(prev, next) {
  if (!Array.isArray(prev) || !Array.isArray(next)) return false;
  if (prev.length !== next.length) return false;
  return prev.every((c, i) => {
    const b = next[i];
    return (
      Boolean(c?.ldsSlotLocked) === Boolean(b?.ldsSlotLocked) &&
      Boolean(c?.paid) === Boolean(b?.paid) &&
      round2(Number(c?.amount) || 0) === round2(Number(b?.amount) || 0) &&
      Number(c?.numberofpayments || 1) === Number(b?.numberofpayments || 1)
    );
  });
}

export const CardsProvider = ({
  totalPrice,
  pricingAuditMeta,
  ldsSplitOrder = null,
  children,
}) => {
  const [cards, setCards] = useState([
    { ...EMPTY_CARD, amount: totalPrice, amountWithInterest: totalPrice },
  ]);
  const [activeCardIndex, setActiveCardIndex] = useState(0);
  const { interestTable } = useAppContext();
  const fillThenPayPrefsConsumedRef = useRef(false);

  const ldsOrderNormalized = useMemo(() => {
    if (!ldsSplitOrder || typeof ldsSplitOrder !== "object") return null;
    const payments = parseLdsOrderPayments(ldsSplitOrder);
    if (payments.length === 0) return ldsSplitOrder;
    return { ...ldsSplitOrder, payments };
  }, [ldsSplitOrder]);

  const ldsFingerprint = useMemo(
    () => ldsSplitOrderCardsFingerprint(ldsOrderNormalized ?? ldsSplitOrder),
    [ldsOrderNormalized, ldsSplitOrder]
  );

  const hasLdsPayments = useMemo(() => {
    return (
      ldsOrderNormalized &&
      Array.isArray(ldsOrderNormalized.payments) &&
      ldsOrderNormalized.payments.length > 0
    );
  }, [ldsOrderNormalized]);

  useEffect(() => {
    const next = round2(Number(totalPrice) || 0);

    if (hasLdsPayments) {
      const orderForBuild = ldsOrderNormalized ?? ldsSplitOrder;
      const built = buildLdsSplitCardsFromOrder(
        orderForBuild,
        next,
        interestTable,
        pricingAuditMeta
      );
      setCards((prev) => {
        const merged = mergeLdsSplitCardsAgentPrefs(
          built,
          prev,
          orderForBuild,
          interestTable,
          pricingAuditMeta
        );
        if (cardsStructureMatches(prev, merged)) return prev;
        return merged;
      });
      const fin = resolveLdsFinancialState(orderForBuild);
      traceLdsSplitPaymentsSsot({
        order: orderForBuild,
        builtCards: built,
        activeCardIndex: resolveLdsActiveCardIndex(built, 0, orderForBuild),
        orderTotal: fin.totalAmount,
      });
      return;
    }

    if (!fillThenPayPrefsConsumedRef.current) {
      const prefs = consumeHrFillThenPayPaymentPrefs();
      fillThenPayPrefsConsumedRef.current = true;
      if (prefs && Number(prefs.cardCount) > 0) {
        setCards(
          buildSplitCardsFromPrefs(next, prefs, interestTable, pricingAuditMeta)
        );
        return;
      }
    }

    const priced = runPaymentInstallmentPricing({
      principalIls: next,
      numberOfPayments: 1,
      interestTable,
      pricingAuditMeta: compactPricingAuditMeta({
        ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
          ? pricingAuditMeta
          : {}),
        installmentsSelected: 1,
      }),
    });
    setCards((prev) => {
      if (
        prev.length === 1 &&
        !prev[0]?.paid &&
        !prev[0]?.ldsSlotLocked
      ) {
        const nPay = Math.min(12, Math.max(1, Number(prev[0]?.numberofpayments || 1)));
        if (round2(Number(prev[0]?.amount) || 0) === next) {
          return prev;
        }
        const repriced = runPaymentInstallmentPricing({
          principalIls: next,
          numberOfPayments: nPay,
          interestTable,
          pricingAuditMeta: compactPricingAuditMeta({
            ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
              ? pricingAuditMeta
              : {}),
            installmentsSelected: nPay,
          }),
        });
        return [
          {
            ...prev[0],
            amount: next,
            numberofpayments: nPay,
            paymentsInterest: repriced.paymentInterest,
            paymentInterest: repriced.paymentInterest,
            amountWithInterest: repriced.amountWithInterest,
            paymentAction: repriced.paymentAction,
            paymentPricingAuditSteps: repriced.auditSteps,
          },
        ];
      }
      // Keep multi-card handoff selection when total refreshes.
      if (prev.length > 1 && prev.every((c) => !c?.paid && !c?.ldsSlotLocked)) {
        return prev;
      }
      return [
        {
          ...EMPTY_CARD,
          amount: next,
          numberofpayments: 1,
          paymentsInterest: priced.paymentInterest,
          paymentInterest: priced.paymentInterest,
          amountWithInterest: priced.amountWithInterest,
          paymentAction: priced.paymentAction,
          paymentPricingAuditSteps: priced.auditSteps,
        },
      ];
    });
  }, [totalPrice, interestTable, pricingAuditMeta, ldsFingerprint, hasLdsPayments, ldsOrderNormalized, ldsSplitOrder]);

  useEffect(() => {
    if (!hasLdsPayments) {
      setActiveCardIndex(0);
      return;
    }
    setActiveCardIndex((prev) => {
      const resolved = resolveLdsActiveCardIndex(
        cards,
        prev,
        ldsOrderNormalized ?? ldsSplitOrder
      );
      return resolved >= 0 ? resolved : -1;
    });
  }, [cards, ldsFingerprint, hasLdsPayments, ldsOrderNormalized, ldsSplitOrder]);

  const allCardsInterest = (() => {
    let totalCardsFee = 0;
    cards.forEach((currentCard) => {
      const pi = Number(currentCard.paymentInterest);
      if (Number.isFinite(pi)) totalCardsFee += pi;
    });
    return totalCardsFee;
  })();

  const setCardPaid = (cardIndex) => {
    let tempCards = JSON.parse(JSON.stringify(cards));
    tempCards[cardIndex].paid = true;
    setCards(tempCards);
  };

  const updateCards = (tmpCards) => {
    setCards((prev) => {
      const newCards = tmpCards.map((card, index) => {
        const existing = prev[index];
        if (existing?.ldsSlotLocked) {
          return { ...existing };
        }
        if (hasLdsPayments && ldsOrderNormalized?.payments?.[index]) {
          const payment = ldsOrderNormalized.payments[index];
          if (isLdsSplitPaymentSlotLocked(payment, ldsOrderNormalized, index)) {
            return existing ? { ...existing } : { ...card, ldsSlotLocked: true, paid: true };
          }
        }
        const newCard = { ...card };
        newCard.amount = parseFloat(newCard.amount);
        const priced = runPaymentInstallmentPricing({
          principalIls: newCard.amount,
          numberOfPayments: newCard.numberofpayments,
          interestTable,
          pricingAuditMeta: compactPricingAuditMeta({
            ...(pricingAuditMeta && typeof pricingAuditMeta === "object"
              ? pricingAuditMeta
              : {}),
            installmentsSelected: Math.max(
              1,
              Math.floor(Number(newCard.numberofpayments)) || 1
            ),
          }),
        });
        newCard.paymentInterest = priced.paymentInterest;
        newCard.paymentsInterest = priced.paymentInterest;
        newCard.amountWithInterest = priced.amountWithInterest;
        newCard.paymentAction = priced.paymentAction;
        newCard.paymentPricingAuditSteps = priced.auditSteps;
        return newCard;
      });
      return newCards;
    });
  };

  return (
    <CardsContext.Provider
      value={{
        cards,
        updateCards,
        setCardPaid,
        allCardsInterest,
        interestTable,
        activeCardIndex,
        setActiveCardIndex,
        isLdsSplitOrder: hasLdsPayments,
      }}
    >
      {children}
    </CardsContext.Provider>
  );
};

export const useCards = () => {
  const context = useContext(CardsContext);
  if (context === undefined) {
    throw new Error("useCards must be used within a CardsProvider");
  }
  return context;
};

export default CardsContext;
